/**
 * OpportunityFinding — L2 "math laid bare" + L3 disposition (Lane 42, design §5;
 * Linear rebuild Lane 207).
 *
 * One finding's frozen math, evidence, AI framing, Family-4 PPI reference,
 * disposition controls, and append-only notes, from
 * GET /api/opportunities/findings/{finding_id}.
 *
 * DISPLAY ONLY — the hard rule of this page: every figure renders VERBATIM
 * from the endpoint's frozen detail_json / finding fields. The UI never
 * subtracts, multiplies, or re-derives a number (the gross is displayed from
 * detail_json.math.gross, never recomputed from typical minus best).
 *
 * L3 updates are CONFIRMED, not optimistic: controls disable while saving and
 * the UI updates from the server's response body (Rule 111 — the endpoint
 * echoes every field that drives this UI).
 *
 * Lane 207 (Linear direction — docs/design/mockups/opportunity-finding-linear.html,
 * Ryan rev-1): presentation-only. Material-first hero (the material number
 * leads as a mono chip), the full-width evidence charts lead, and the frozen
 * math sits as a compact subordinate panel above them. `OpportunityPriceChart`
 * is a carve-out (untouched -- but see Lane 1358 below); every math/facts/signal string is preserved
 * verbatim so the L2 suite stays green. The page moves onto the `.opp`
 * page-scoped Linear token/class layer (Opportunities.linear.css, light-only).
 *
 * NOTE on that carve-out (Lane 824): it was a PRESENTATION carve-out for the
 * Lane 207/229/257 restyles, and it is no longer total. Lane 824 changed ONE
 * string inside the chart -- the per-PO tooltip marker, "flagged unusual" ->
 * "excluded from scale" -- because that marker was a second, contradicting
 * answer to "which PO is unusual" on a page that must answer it once. No chart
 * geometry, capping, y-domain or figure changed. Read the carve-out as "do not
 * restyle the chart", not as "the chart may never be corrected".
 *
 * Lane 257 (QA2-5): restored the Lane-207 full-width-charts arrangement above.
 * Lane 229 had put the math + charts side by side, which squeezed the evidence
 * charts into a ~60% column — cramped on consolidation findings whose math rail
 * is tall (Ryan feedback, 2026-07-19). Layout only; figures byte-identical.
 *
 * Lane 1358 (Ryan, 2026-09-30, from a mockup he approved): the math panel is
 * renamed "Findings" and spans the full page width; the two charts sit side by
 * side as two equal blocks (chart beside CHART, not math beside chart, so the
 * Lane 229 squeeze does not return); the Blended PPI paragraph moves into a
 * hover note on its key; the confidence line, the "The evidence" heading and
 * the notes under the PO table are removed. Display only; no figure changes.
 *
 * Classified findings (finding_class 'monitor'/'verify') render claim-free:
 * neutral badge instead of a dollar headline, an honest facts card (PRICES
 * only — never the internal savings figure, Consult #3 BC-1), notes-only
 * (disposition buttons hidden; the server also refuses with 422).
 *
 * Lane 355 (O-2): CLASS_MONITOR has TWO flavors since Lane 352 — the original
 * suppression flavor ("already at your best price") and the Rule-B price-gone
 * flavor ("the best price is no longer obtainable"). The badge + facts card
 * branch on `detail_json.price_gone`; telling a buyer whose price nearly
 * tripled that no action is needed was the defect this closes.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';
import { formatValue } from '../utils/formatValue';
import {
  FAMILY_LABELS, DISPOSITION_LABELS,
  formatUsd, formatUnitPrice, formatQty, headlineLabel, formatDate,
} from '../utils/opportunityDisplay';
import OpportunityPriceChart from '../components/charts/OpportunityPriceChart';
import {
  ResponsiveContainer, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { colors } from '../components/SharedComponents';
import { windowPurchases, evidenceWindowMonths, chartAsOf } from '../utils/opportunityChart';
import { pluralize } from '../utils/pluralize';
import './Opportunities.linear.css';

const STATUS_HELP = {
  pursuing: "We're acting on this.",
  needs_data: "We've asked the team or supplier for more information.",
  accepted: 'Reviewed - no action needed.',
};

// SAFE/WATCH/RISKY (inventory) + ALIGNED/PADDED/UNDERSTATED (lead time) -> the
// shared signal-badge tone class.
const SIGNAL_TONE = {
  SAFE: 'good', WATCH: 'warn', RISKY: 'crit',
  ALIGNED: 'good', PADDED: 'warn', UNDERSTATED: 'crit',
};

// Claim-free classes -> the meta-strip badge (tone class + label). Lane 338
// adds 'evidence' (step-change: a supplier-discussion talking point, neutral
// accent tone — evidence is not a warning).
const CLASS_BADGES = {
  monitor: { cls: 'good', label: 'At best price - monitor' },
  verify: { cls: 'warn', label: 'Verify data' },
  evidence: { cls: 'neutral', label: 'Supplier discussion point' },
};

// Lane 355 (O-2): CLASS_MONITOR gained a SECOND flavor in Lane 352 — the best
// price is GONE (detail_json.price_gone), as opposed to the original
// suppression flavor (detail_json.suppression, "already at your best price").
// The monitor badge + facts note above are the suppression story and are FALSE
// for a price-gone row (e.g. 590949: best $78.75, cheapest since $257.78,
// +227%). One helper owns the discrimination so the badge and the card can
// never disagree. Pre-Lane-352 snapshots carry neither key -> false -> today.
const MONITOR_PRICE_GONE_BADGE = { cls: 'neutral', label: 'Best price gone - monitor' };
function isPriceGone(finding) {
  return Boolean((finding.detail_json || {}).price_gone);
}

// One labeled math row: plain-language label + the frozen figure, verbatim.
function MathRow({ label, value, emphasize }) {
  return (
    <div className="opp-mrow">
      <span className="lab">{label}</span>
      <span className={`val${emphasize ? ' emph' : ''}`}>{value}</span>
    </div>
  );
}

// A small hairline evidence/history table (Unit price / Quantity, or the full
// purchase-history columns). Replaces the shared DataTable so the chrome moves
// onto Linear tokens; the rendered cell text is unchanged.
function EvidenceTable({ rows }) {
  return (
    <table className="opp-tbl">
      <thead><tr><th>Unit price</th><th className="right">Quantity</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>{formatUnitPrice(r.unit_price)}</td>
            <td className="num">{formatQty(r.quantity)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// THE PURCHASE ORDERS BEHIND THIS FINDING (Lane 821).
//
// Ryan, live QA 2026-08-04: "in the PO that looks weird, where that one price
// has spiked -- what data do we have about that specific PO? ... which
// warehouse or which buyer." The frozen detail_json evidence carries only
// unit_price + quantity, so the site, the PO number and the dates come back
// from GET /api/opportunities/findings/{id}/purchase-orders, which re-reads
// purchase_orders on the finding's OWN staged universe.
//
// Lane 852: this used to be one of TWO per-PO tables on the page, with the
// live charts sandwiched between them. Ryan, QA 2026-08-05: "You only need
// that once. And then you see the charts... and then there's fucking purchase
// history again below that." The page now renders exactly ONE PO table.
// Lane 858 moved it BELOW the charts on Ryan's follow-up QA ("I think I like
// the chart first and then the POs") -- Lane 852 had put it above. See the
// render block for the consolidation and for what the material-wide table's
// Supplier column was traded for.
//
// Does the finding's OWN PO table actually render? `findingPos` is an
// INDEPENDENT fetch from `priceHistory` (both fail quietly, separately), so the
// material-wide table stays as the FALLBACK for exactly the case where this one
// has nothing to show. One predicate decides which of the two renders, so they
// can never both appear and never both be absent while data exists (this
// predicate is Lane 824's, re-pointed from a note to the render gate).
function findingPoTableRenders(data) {
  return Boolean(data && data.purchase_orders && data.purchase_orders.length > 0);
}

function FindingPurchaseOrdersCard({ data }) {
  if (!data) return null;
  const { purchase_orders: rows, meta } = data;

  if (!rows || rows.length === 0) {
    if (!meta || !meta.unavailable_reason) return null;
    return (
      <section className="opp-sec">
        <div className="opp-sec-h"><span className="opp-lbl">Purchase orders behind this finding</span></div>
        <div className="opp-note">{meta.unavailable_reason}</div>
      </section>
    );
  }

  const showOrderDate = meta.order_date_count > 0;
  // Say what po_date IS rather than guessing. The basis is derived per tenant
  // (po_date == receipt_date is an XMEK feed property, not a platform one), so
  // where we cannot tell, the column stays neutral instead of asserting.
  const dateHeader = meta.po_date_is_receipt_date === true ? 'Received' : 'PO date';
  // Lane 1358 (Ryan, 2026-09-30): the footer of notes under this table is gone
  // ("get rid of all that"). What it said that a buyer still needs moved, it
  // was not dropped: the date-column definition is the column header's hover
  // text (the product-voice ruling's "column-header tooltip"), and a truncated
  // table says so in its heading, so "(N POs)" never reads as the whole set.
  // The rest (who raised the requisition, other-unit lines, the every-supplier
  // count, partial order dates, the no-single-line-flagged line) is removed on
  // his instruction; the chart above still plots every supplier's purchases.
  const dateHeaderHelp = meta.po_date_is_receipt_date === true
    ? 'The date on the PO line in your feed. It matches the receipt date on these lines. It is not the date the order was placed.'
    : 'The date carried on the PO line in your feed. It is not confirmed whether this is the order date or the receipt date.';
  const countText = meta.truncated
    ? `${meta.row_count} most recent of ${pluralize(meta.staged_row_count, 'PO')}`
    : pluralize(rows.length, 'PO');

  return (
    <section className="opp-sec">
      <div className="opp-sec-h">
        <span className="opp-lbl">
          Purchase orders behind this finding ({countText})
        </span>
      </div>
      <table className="opp-tbl">
        <thead>
          <tr>
            <th>PO number</th>
            <th>Warehouse</th>
            <th>Business unit</th>
            <th title={dateHeaderHelp}>{dateHeader}</th>
            {showOrderDate && <th>Ordered</th>}
            <th className="right">Quantity</th>
            <th className="right">Unit price</th>
            <th className="right">Line value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.po_number} className={p.is_evidence ? 'opp-flagged' : undefined}>
              <td className="opp-supcell">{p.po_number || '-'}</td>
              {/* The raw site string, never the abbreviation alone — Analytics
                  spells it "Warehouse 31" and one page must not invent "W31"
                  as a second name for the same place. */}
              <td>{p.manufacturing_site || '-'}</td>
              <td className="opp-supcell">{p.business_unit || '-'}</td>
              <td>{formatDate(p.po_date)}</td>
              {showOrderDate && <td>{p.order_date ? formatDate(p.order_date) : '-'}</td>}
              <td className="num">{formatQty(p.purchase_quantity)}</td>
              <td className="num">
                {p.is_evidence ? `${formatUnitPrice(p.unit_price)} (flagged)` : formatUnitPrice(p.unit_price)}
              </td>
              <td className="num">{formatUsd(p.total_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// Claim-free facts card for classified findings (Lane 47 plan §1D). PRICES
// ONLY, verbatim from the frozen reason blocks — this card must NEVER read
// math.savings / math.annual_spend (Consult #3 BC-1: the internal figure
// exists in detail_json for flip-back continuity but is never rendered).
function ClassifiedFactsCard({ finding }) {
  const detail = finding.detail_json || {};
  const math = detail.math || {};
  let rows = [];
  let note = '';

  if (finding.finding_class === 'monitor' && isPriceGone(finding)) {
    // Lane 355 (O-2) / Lane 352 Rule-B: the price this finding was chasing is
    // no longer on offer. Every value renders VERBATIM from the frozen
    // price_gone block — nothing is recomputed here (Rule 116), and no dollar
    // or savings figure renders (this row claims nothing).
    const pg = detail.price_gone || {};
    rows = [
      { label: 'Best price you paid', value: formatUnitPrice(pg.best_price ?? math.best_price) },
      { label: 'Last paid at that price', value: formatDate(pg.best_last_seen) },
      { label: 'Purchases since', value: pg.pos_since_best != null ? pluralize(pg.pos_since_best, 'purchase') : 'Not available' },
      { label: 'Cheapest price since', value: formatUnitPrice(pg.min_price_since_best) },
      { label: 'Premium over your best price', value: formatValue(pg.premium_pct, 'pct_signed') },
    ];
    note = 'Your best price has not been available since - no savings are '
      + 'claimed against a price that is not currently on offer. Worth raising '
      + 'with the supplier. If a later order matches it, this resurfaces '
      + 'automatically.';
  } else if (finding.finding_class === 'monitor') {
    const sup = detail.suppression || {};
    rows = [
      { label: 'Most recent purchase', value: formatDate(sup.latest_po_date || math.latest_po_date) },
      { label: 'Price on that purchase', value: formatUnitPrice(sup.latest_date_min_price ?? math.latest_date_min_price) },
      { label: 'Your best price (last 12 months)', value: formatUnitPrice(sup.min_price_12mo ?? math.min_price_12mo) },
    ];
    note = 'You are already buying at your best price - no action needed. '
      + 'If later orders drift back up, this resurfaces automatically.';
  } else if (finding.family_type === 'step_change' && finding.finding_class === 'evidence') {
    // Lane 338 (the Lane 323 step-change family): a sustained price step-up,
    // surfaced as SUPPLIER-DISCUSSION EVIDENCE (Ryan-locked reframe — nothing
    // to DO on it, so it is never framed as a savings opportunity). Every
    // value renders verbatim from the frozen detail_json; NO dollar renders
    // here (the engine's annualized figure stays supporting detail — the
    // engine-authored framing above carries the story).
    rows = [
      { label: 'Price before the step (base level)', value: formatUnitPrice(math.base_price) },
      { label: 'Price now (current level)', value: formatUnitPrice(math.current_price) },
      { label: 'Increase', value: formatValue(math.step_pct, 'pct_signed') },
      { label: 'Stepped up starting', value: formatDate(detail.step_month) },
    ];
    if (detail.months_at_new_level != null) {
      rows.push({ label: 'Held at the new level', value: pluralize(detail.months_at_new_level, 'month') });
    }
    // The real ladder when it moved in more than one step ("$59.95 → $79.94
    // → $89.86") — frozen plateau levels, formatting only.
    if (detail.rung_count > 2 && Array.isArray(detail.ladder) && detail.ladder.length > 0) {
      rows.push({
        label: `Price ladder (${detail.rung_count - 1} steps)`,
        value: detail.ladder.map((l) => formatUnitPrice(l.level)).join(' → '),
      });
    }
    note = 'This is a talking point for your next supplier conversation - a '
      + 'sustained price step-up presented as evidence. No savings or recovery '
      + 'dollars are claimed.';
  } else if (finding.family_type === 'step_change') {
    // Lane 338: a step demoted to verify (>=10x base-to-current) is a
    // UOM/pack-size artifact signature, not a real price move. Claim-free
    // spread facts from the step's OWN math; NO dollar renders. Same verify
    // note as the volume/UOM and spike paths.
    rows = [
      { label: 'Price before the step', value: formatUnitPrice(math.base_price) },
      { label: 'Price after the step', value: formatUnitPrice(math.current_price) },
      { label: 'Price spread', value: math.excursion_multiple ? `${formatQty(math.excursion_multiple)}x range` : 'Not available' },
    ];
    note = 'A spread this extreme usually signals a unit-of-measure or '
      + 'pack-size change in the data rather than a price opportunity. '
      + 'Verify the unit of measure before relying on any savings math. '
      + 'No savings are claimed for this finding.';
  } else if (finding.family_type === 'spike_reversion') {
    // Lane 258: a spike demoted to verify (>=10x its neighbors) is a
    // UOM/pack-size artifact, not a recoverable overcharge. Claim-free spread
    // facts from the spike's OWN math (there is no verify_uom block on a spike
    // finding); NO dollar renders. Same verify note as the volume/UOM path.
    rows = [
      { label: 'Surrounding price', value: formatUnitPrice(math.baseline_price) },
      { label: 'Price on the flagged date', value: formatUnitPrice(math.spike_price) },
      { label: 'Price spread', value: math.excursion_multiple ? `${formatQty(math.excursion_multiple)}x range` : 'Not available' },
    ];
    note = 'A spread this extreme usually signals a unit-of-measure or '
      + 'pack-size change in the data rather than a price opportunity. '
      + 'Verify the unit of measure before relying on any savings math. '
      + 'No savings are claimed for this finding.';
  } else {
    const uom = detail.verify_uom || {};
    rows = [
      { label: 'Typical PO price (median)', value: formatUnitPrice(uom.typical_price ?? math.typical_price) },
      { label: 'Lowest qualifying price', value: formatUnitPrice(uom.best_price ?? math.best_price) },
      { label: 'Price spread', value: uom.spread_ratio ? `${formatQty(uom.spread_ratio)}x range` : 'Not available' },
    ];
    note = 'A spread this extreme usually signals a unit-of-measure or '
      + 'pack-size change in the data rather than a price opportunity. '
      + 'Verify the unit of measure before relying on any savings math. '
      + 'No savings are claimed for this finding.';
  }

  // A2.3: PO-count as a plain fact (replaces the "seen Nx" hero badge).
  if (detail.point_count) {
    const win = (detail.window_months || {}).typical || 24;
    rows.push({ label: `Purchases (last ${win} months)`, value: formatQty(detail.point_count) });
  }

  return (
    <div>
      <div className="opp-lbl" style={{ marginBottom: '8px' }}>The facts</div>
      {rows.map((r) => (
        <MathRow key={r.label} label={r.label} value={r.value} />
      ))}
      <div className="opp-note">{note}</div>
    </div>
  );
}

// The per-family math walk-through, all values verbatim from detail_json.
function MathCard({ finding }) {
  const detail = finding.detail_json || {};
  const math = detail.math || {};
  const windows = detail.window_months || {};

  let rows = [];
  let evidence = null;
  let footnote = null;

  // A3 item 2 (Ryan decision (b)): the FROZEN 12-month PO count renders on
  // Lane-46-shape volume cards, directly below the 12-month spend it
  // describes. This ONE boolean drives both the render and the 24-month
  // fallback suppression below, so the count row can never silently vanish
  // (/review fix: suppression follows the render, not bare field presence).
  const renders12moCount = finding.family_type === 'volume'
    && math.weighted_avg_price !== undefined && math.weighted_avg_price !== null
    && Boolean(detail.point_count_12mo);

  if (finding.family_type === 'volume') {
    // Lane 46: the savings basis is the 12-month spend-weighted average paid
    // minus the best price (the 24-month median is DISPLAY-ONLY context).
    // Findings persisted by PRE-Lane-46 snapshots carry the old shape
    // (math.gross, no weighted_avg_price) — render those with the old rows
    // so a stale finding never shows blank/mislabeled figures (Consult #1
    // condition C3).
    const isLane46Shape = math.weighted_avg_price !== undefined && math.weighted_avg_price !== null;
    if (isLane46Shape) {
      rows = [
        { label: 'Weighted average paid (last 12 months)', value: formatUnitPrice(math.weighted_avg_price) },
        { label: 'Spent (last 12 months)', value: formatUsd(math.annual_spend) },
        ...(renders12moCount
          ? [{ label: `Purchases (last ${windows.annual_volume || 12} months)`,
               value: formatQty(detail.point_count_12mo) }]
          : []),
        { label: 'Your best price', value: formatUnitPrice(math.best_price) },
        { label: 'Order size at the best price', value: `${formatQty(math.best_qty)} units` },
        { label: 'Your usual order size', value: `${formatQty(math.median_order_qty)} units` },
        { label: 'Units bought (last 12 months)', value: formatQty(math.annual_volume) },
        { label: 'Typical PO price (median - context only)', value: formatUnitPrice(math.typical_price) },
        { label: 'Potential savings (gross, per year)', value: formatUsd(math.savings), emphasize: true },
      ];
    } else {
      rows = [
        { label: 'You typically pay', value: formatUnitPrice(math.typical_price) },
        { label: 'Your best price', value: formatUnitPrice(math.best_price) },
        { label: 'Order size at the best price', value: `${formatQty(math.best_qty)} units` },
        { label: 'Your usual order size', value: `${formatQty(math.median_order_qty)} units` },
        { label: 'Units bought per year', value: formatQty(math.annual_volume) },
        { label: 'Potential savings (gross, per year)', value: formatUsd(math.gross), emphasize: true },
      ];
    }
    if (math.net !== undefined && math.net !== null) {
      rows.push(
        { label: 'Carrying-cost rate', value: formatValue(math.carrying_cost_rate * 100, 'pct', { pctDigits: 0 }) },
        { label: 'Cost of holding bigger orders', value: formatUsd(math.holding_cost) },
        { label: 'Net savings (per year)', value: formatUsd(math.net), emphasize: true },
      );
    } else {
      footnote = 'Gross figure - carrying cost not included.';
    }
  } else if (finding.family_type === 'dispersion') {
    rows = [
      { label: 'Typical price (median)', value: formatUnitPrice(math.median_price) },
      { label: 'Typical variation (MAD)', value: formatUnitPrice(math.mad) },
      { label: 'Purchases flagged as unusually high', value: formatQty(math.num_high_outliers) },
      { label: 'Paid over the typical price', value: formatUsd(math.overpayment), emphasize: true },
    ];
    evidence = { title: 'Flagged purchases', rows: detail.outliers || [] };
  } else if (finding.family_type === 'same_day') {
    rows = [
      { label: 'Date', value: formatDate(detail.incident_date || finding.incident_date) },
      { label: 'Lowest price that day', value: formatUnitPrice(math.min_price) },
      { label: 'Purchases that day', value: formatQty(math.buy_count) },
      { label: "Paid over the day's lowest price", value: formatUsd(math.overpayment), emphasize: true },
    ];
    evidence = { title: "That day's purchases", rows: detail.buys || [] };
  } else if (finding.family_type === 'leadtime') {
    // Lane 148: every value below is the frozen engine figure — display only.
    const days = (v) => (v != null ? `${v} days` : 'Not available');
    rows = [
      { label: 'Displayed lead time', value: days(math.displayed_lt_days) },
      { label: 'Actual lead time (avg of receipts)', value: days(math.actual_avg_lt_days) },
      { label: 'Gap (actual - displayed)', value: days(math.variance_days) },
      { label: 'Receipts behind the average', value: formatQty(math.po_count) },
      { label: 'Spend (last 12 months)', value: formatUsd(math.spend_12mo) },
    ];
    if (math.excess_stock_dollars != null) {
      rows.push({ label: 'Tied up in early-ordered stock (approx.)',
                  value: formatUsd(math.excess_stock_dollars), emphasize: true });
      footnote = 'Approximation: daily spend x days of padding. The real lead '
        + 'time is shorter than the system shows, so orders go out earlier '
        + 'than needed.';
    } else if (math.at_risk_spend_12mo != null) {
      rows.push({ label: 'Spend exposed to stockouts', value: formatUsd(math.at_risk_spend_12mo) });
      footnote = 'The real lead time runs longer than the system shows - '
        + 'orders planned on the displayed number arrive late.';
    }
  } else if (finding.family_type === 'spike_reversion') {
    // Lane 246/258: a transient excursion that reverts (the one-off
    // billing-error signature). All values are the frozen engine figures —
    // display only, modeled on the same_day incident card. baseline_price =
    // the HIGHER of the immediate before/after neighbors (the conservative
    // claim); overpayment = the per-PO excess over it on the spike date.
    const before = detail.before || {};
    const after = detail.after || {};
    rows = [
      { label: 'Spike date', value: formatDate(detail.incident_date || finding.incident_date) },
      { label: 'Surrounding price (before & after)', value: formatUnitPrice(math.baseline_price) },
      { label: 'Price on the spike date', value: formatUnitPrice(math.spike_price) },
      { label: 'Paid over the surrounding price', value: formatUsd(math.overpayment), emphasize: true },
    ];
    evidence = { title: 'Purchases on the spike date', rows: detail.buys || [] };
    footnote = 'The surrounding price is the higher of the orders just before ('
      + `${formatDate(before.date)}, ${formatUnitPrice(before.price)}) and just after (`
      + `${formatDate(after.date)}, ${formatUnitPrice(after.price)}) - the conservative `
      + 'baseline. The price returned to it on the next order.';
  }

  // A2.3: PO-count as a plain fact. same_day findings carry no point_count —
  // the row simply doesn't render. A3 item 2: skipped only when the 12-month
  // count row ACTUALLY rendered above (renders12moCount).
  if (detail.point_count && !renders12moCount) {
    rows.push({ label: `Purchases (last ${windows.typical || 24} months)`,
                value: formatQty(detail.point_count) });
  }

  // Lane 1358 (Ryan, 2026-09-30): the panel spans the full page width, so the
  // evidence table sits BESIDE the figures (right column) instead of under
  // them. Second pass (Ryan, same day, on PROD): the evidence heading is level
  // with "Findings" and the table rows are level with the figure rows (both
  // columns share one row height, .opp-mathgrid in Opportunities.linear.css);
  // the outlier-test footnote and the "Window: ..." line are removed (the
  // "Purchases (last N months)" row already names the window). The other
  // families' footnotes are unchanged. Same rows, same figures; layout only.
  const hasEvidence = Boolean(evidence && evidence.rows.length > 0);
  return (
    <div className={`opp-mathgrid${hasEvidence ? ' two' : ''}`}>
      <div>
        <div className="opp-lbl opp-mathhd">Findings</div>
        {rows.map((r) => (
          <MathRow key={r.label} label={r.label} value={r.value} emphasize={r.emphasize} />
        ))}
        {footnote && <div className="opp-note">{footnote}</div>}
      </div>
      {hasEvidence && (
        <div>
          <div className="opp-lbl opp-mathhd">{evidence.title}</div>
          <EvidenceTable rows={evidence.rows} />
        </div>
      )}
    </div>
  );
}

// Family 4 by reference (design §1.5): the EXISTING PPI gap, labeled as a
// separate market signal. Rendered only when the backend sent it.
function MarketContextCard({ family4 }) {
  if (!family4) return null;
  return (
    <div className="opp-signal">
      <div className="sh"><span className="opp-lbl">Market context - a separate signal</span></div>
      <MathRow label="Your price change vs this supplier" value={formatValue(family4.your_pct, 'pct_signed')} />
      <MathRow label="Market benchmark (PPI)" value={formatValue(family4.market_pct, 'pct_signed')} />
      <MathRow label="Gap" value={formatValue(family4.gap_pct, 'pct_signed')} emphasize />
      <div className="opp-note">
        {`Based on ${formatValue(family4.spend_coverage_pct, 'pct')} of spend with this supplier - confidence: ${family4.confidence}. `}
        This compares supplier price movement to a market index; it is separate from the opportunity math above.
      </div>
    </div>
  );
}

// Lane 73: the per-material inventory position (Option A, display-only) — a
// SEPARATE signal frozen into detail_json.inventory at snapshot time. It never
// alters the opportunity math above; it tells the buyer whether the part's REAL
// stock position supports consolidating. Rendered only when the backend froze a
// block (Family-1 volume, flag ON at generation).
const INVENTORY_VERDICTS = {
  SAFE: 'This part moves quickly - ordering bigger is low-risk on carrying '
    + 'cost and can reduce stockout risk.',
  WATCH: 'You already hold a meaningful stock of this part - weigh the price '
    + 'saving against the carrying cost of a bigger batch.',
  RISKY: 'A bigger batch adds to stock you are not using quickly - weigh the '
    + 'price saving against the carrying cost before consolidating.',
};

function InventoryPositionCard({ finding }) {
  const inv = (finding.detail_json || {}).inventory;
  if (!inv) return null;
  const verdict = INVENTORY_VERDICTS[inv.chip]
    || 'No inventory signal could be computed for this part (missing stock or usage data).';
  return (
    <div className="opp-signal">
      <div className="sh">
        <span className="opp-lbl">Inventory position - a separate signal</span>
        <span className={`badge ${SIGNAL_TONE[inv.chip] || 'mut'}`}>{inv.chip || 'No signal'}</span>
      </div>
      {inv.reason && <div className="opp-note" style={{ marginTop: 0, marginBottom: '8px' }}>{inv.reason}</div>}
      <MathRow
        label="On hand"
        value={inv.on_hand_qty != null
          ? pluralize(inv.on_hand_qty, 'unit')
            + (inv.on_hand_dollar != null ? ` (${formatUsd(inv.on_hand_dollar)})` : '')
          : 'Not available'}
      />
      <MathRow
        label="Usage (last 12 months)"
        value={inv.usage_12mo != null ? pluralize(inv.usage_12mo, 'unit') : 'Not available'}
      />
      <MathRow
        label="Stock on hand"
        value={inv.cover_months != null ? `~${pluralize(inv.cover_months, 'month')}` : 'Not available'}
        emphasize
      />
      <MathRow
        label="Slow/obsolete flagged"
        value={inv.osmi_dollar > 0 ? formatUsd(inv.osmi_dollar) : 'none'}
      />
      <div className="opp-note">
        {verdict}
        {' '}This is a separate signal from real stock data; it does not change the savings math above.
        {inv.snapshot_date && ` Inventory as of ${formatDate(inv.snapshot_date)}.`}
      </div>
    </div>
  );
}

// Lane 148: the per-pair lead-time reality (display-only) — a SEPARATE signal
// frozen into detail_json.leadtime at snapshot time. It never alters the
// opportunity math above; it tells the buyer whether this supplier's REAL
// delivery lead time on this part matches the number the system shows.
const LEADTIME_VERDICTS = {
  ALIGNED: 'The lead time your system shows for this pair matches what deliveries '
    + 'actually take.',
  PADDED: 'Deliveries actually arrive faster than the system shows - the displayed '
    + 'number is padded, so orders based on it go out earlier than needed.',
  UNDERSTATED: 'Deliveries actually take longer than the system shows - orders '
    + 'planned on the displayed number arrive late. Weigh that before consolidating '
    + 'volume onto this supplier.',
};

function LeadtimeRealityCard({ finding }) {
  const lt = (finding.detail_json || {}).leadtime;
  if (!lt) return null;
  const days = (v) => (v != null ? `${v} days` : 'Not available');
  const verdict = LEADTIME_VERDICTS[lt.chip]
    || 'No lead-time signal could be computed for this pair (missing receipts or displayed lead time).';
  return (
    <div className="opp-signal">
      <div className="sh">
        <span className="opp-lbl">Lead-time reality - a separate signal</span>
        <span className={`badge ${SIGNAL_TONE[lt.chip] || 'mut'}`}>{lt.chip || 'No signal'}</span>
      </div>
      {lt.reason && <div className="opp-note" style={{ marginTop: 0, marginBottom: '8px' }}>{lt.reason}</div>}
      <MathRow label="Displayed lead time" value={days(lt.displayed_lt_days)} />
      <MathRow label="Actual lead time (avg of receipts)" value={days(lt.actual_avg_lt_days)} emphasize />
      <MathRow
        label="Receipts behind the average"
        value={lt.po_count ? formatQty(lt.po_count) : 'None'}
      />
      <div className="opp-note">
        {verdict}
        {' '}This is a separate signal from real receipt data; it does not change the savings math above.
      </div>
    </div>
  );
}

// Lane 363: the date-controlled volume-trend evidence view (design §3) — an
// era-striped price-vs-quantity scatter + a deterministic CLAIM-FREE verdict
// line, rendered ONLY from the frozen detail_json.volume_trend block (Rule 116;
// never recomputed, NEVER a fitted trend line — the fitted line IS the date
// confound this view removes). Attached to volume ACTIVE + price-gone MONITOR
// findings at snapshot time; absent key -> renders nothing (pre-fix snapshots
// byte-identical, the Lane 73/148 signal pattern).
const VOLTREND_VERDICT = {
  affirmative: { label: 'Larger orders priced lower', fg: colors.success, bg: colors.successLight },
  negative: { label: 'No volume discount - a date effect', fg: colors.secondaryText, bg: colors.borderLight },
  insufficient: { label: 'Not enough contrast to tell', fg: colors.textMuted, bg: colors.borderLight },
};
// Distinct per-era colors (the scatter's ONLY job is to make the date structure
// visible; cycles for the rare cell with many eras).
const VOLTREND_ERA_COLORS = [
  colors.electricBlue, colors.warning, colors.success, colors.purple,
  colors.cyan, colors.danger, colors.textMuted,
];

function VolumeTrendTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0] && payload[0].payload;
  if (!p) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #E2E5EA', borderRadius: '8px',
      padding: '9px 13px', fontSize: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 600 }}>{formatUnitPrice(p.price)} &middot; {formatQty(p.qty)} units</div>
      {p.po_date && <div style={{ color: colors.textMuted, marginTop: '2px' }}>{formatDate(p.po_date)}</div>}
    </div>
  );
}

function VolumeTrendCard({ finding }) {
  const vt = (finding.detail_json || {}).volume_trend;
  if (!vt || !Array.isArray(vt.points) || vt.points.length === 0) return null;
  const eras = Array.isArray(vt.eras) ? vt.eras : [];
  const verdict = VOLTREND_VERDICT[vt.verdict] || VOLTREND_VERDICT.insufficient;

  // group the frozen points by their (frozen) era index -> one Scatter series
  // per era so each era gets its own color + a legend entry naming its dates.
  const byEra = {};
  vt.points.forEach((p) => { (byEra[p.era] = byEra[p.era] || []).push(p); });
  const eraLabel = (i) => {
    const e = eras[i];
    if (!e || !e.start) return `Era ${i + 1}`;
    return e.end && e.end !== e.start
      ? `${formatDate(e.start)} - ${formatDate(e.end)}`
      : formatDate(e.start);
  };
  const eraIdx = Object.keys(byEra).map(Number).sort((a, b) => a - b);

  return (
    <section className="opp-sec">
      <div className="opp-sec-h">
        <span className="opp-lbl">Price vs. order size - controlled for date</span>
        <span className="note">a separate signal: evidence context, not part of the dollar claim</span>
      </div>
      <div className="opp-panelcard">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, borderRadius: '5px', padding: '3px 9px',
            color: verdict.fg, background: verdict.bg }}>{verdict.label}</span>
        </div>
        <div className="opp-note" style={{ marginTop: 0, marginBottom: '10px' }}>{vt.verdict_line}</div>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart margin={{ top: 10, right: 20, bottom: 24, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F3" />
            <XAxis
              type="number" dataKey="qty" name="Order quantity"
              scale="log" domain={['auto', 'auto']} allowDataOverflow
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickFormatter={(v) => formatQty(v)}
              label={{ value: 'Order quantity (log scale)', position: 'insideBottom', offset: -12, fontSize: 11, fill: '#8A94A6' }}
            />
            <YAxis
              type="number" dataKey="price" name="Unit price"
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickFormatter={(v) => formatUnitPrice(v)} width={70}
            />
            <Tooltip content={<VolumeTrendTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            {eraIdx.map((i) => (
              <Scatter
                key={i} name={eraLabel(i)} data={byEra[i]}
                fill={VOLTREND_ERA_COLORS[i % VOLTREND_ERA_COLORS.length]}
                fillOpacity={0.85}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
        <div className="opp-note">
          Each dot is one purchase, colored by price era. A real volume discount shows as lower
          prices at bigger sizes <em>within</em> one color; a price step over time shows as horizontal
          color bands (the date effect, not size). There is no trend line: a line fitted across
          eras is the exact date confound this view removes.
          {vt.truncated ? ` Showing the ${vt.points.length} most recent of ${vt.truncated} purchases.` : ''}
        </div>
      </div>
    </section>
  );
}

function OpportunityFinding() {
  const { findingId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [finding, setFinding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Lane 47 chart data — both from EXISTING live endpoints (plan §2A + A2.6).
  const [priceHistory, setPriceHistory] = useState(null);   // null = loading/absent
  const [priceImpact, setPriceImpact] = useState(null);     // supplier blend; null = absent

  // Lane 821 — the PO lines behind THIS finding. null = loading/absent.
  const [findingPos, setFindingPos] = useState(null);

  const [savingStatus, setSavingStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [acceptedHint, setAcceptedHint] = useState(false);

  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState(null);

  const fetchFinding = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await fetch(`${API_BASE}/api/opportunities/findings/${findingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) {
        // Finding unknown, cross-tenant, or the module flag flipped OFF —
        // all indistinguishable by design.
        setNotFound(true);
        setFinding(null);
        return;
      }
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setFinding(await res.json());
    } catch (err) {
      console.error('Finding fetch failed:', err.message);
      setError('Unable to load this finding. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, findingId]);

  useEffect(() => { fetchFinding(); }, [fetchFinding]);

  // Lane 821: the PO lines behind this finding. Keyed on findingId alone (the
  // endpoint resolves the material + supplier itself), so it rides alongside
  // the finding fetch rather than behind it. Every failure is a quiet absence —
  // the card simply does not render, exactly like the chart fetches below.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/opportunities/findings/${findingId}/purchase-orders`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setFindingPos(res.ok ? await res.json() : null);
      } catch (err) {
        console.error('Finding purchase-order fetch failed:', err.message);
        if (!cancelled) setFindingPos(null);
      }
    })();
    return () => { cancelled = true; };
  }, [token, findingId]);

  // The page's ONE evidence window + its ONE "as of" anchor (A3 item 3),
  // computed once — the fetch effect below and every render surface (charts, PO
  // table, captions) read these same two values.
  const asOf = chartAsOf(finding);
  const windowMonths = evidenceWindowMonths(finding, asOf);

  // Chart data rides AFTER the finding (it names the material). Failures are
  // quiet fallbacks, never page errors (plan §2E).
  useEffect(() => {
    if (!token || !finding?.material_number) return;
    const material = encodeURIComponent(finding.material_number);
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/materials/${material}/price-history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.ok) {
          const body = await res.json();
          setPriceHistory(body.data || []);
        } else if (!cancelled) {
          setPriceHistory([]);
        }
      } catch (err) {
        console.error('Price history fetch failed:', err.message);
        if (!cancelled) setPriceHistory([]);
      }
    })();

    // A2.6: the market line comes from the SUPPLIER's blended market index —
    // the exact series the supplier dossier renders (one source). Every
    // failure/absence is a quiet fallback, never a page error.
    const supplier = finding?.supplier_display;
    (async () => {
      if (!supplier) return;
      try {
        const res = await fetch(
          `${API_BASE}/api/analytics/supplier-price-impact/${encodeURIComponent(supplier)}?months=${windowMonths}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled && res.ok) {
          setPriceImpact(await res.json());
        } else if (!cancelled) {
          setPriceImpact(null);
        }
      } catch (err) {
        console.error('Supplier price-impact fetch failed:', err.message);
        if (!cancelled) setPriceImpact(null);
      }
    })();

    return () => { cancelled = true; };
  }, [token, finding?.material_number, finding?.supplier_display, windowMonths]);

  const setDisposition = async (newStatus) => {
    if (!finding) return;
    setSavingStatus(newStatus);
    setStatusError(null);
    setAcceptedHint(false);
    try {
      const res = await fetch(
        `${API_BASE}/api/opportunities/dispositions/${finding.identity_hash}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status: newStatus }),
        },
      );
      if (!res.ok) {
        // A 422 carries the server's real explanation (e.g. "decisions apply
        // to active findings only" from a stale tab whose finding flipped to
        // monitor/verify) — surface it instead of the generic message.
        let userMessage = null;
        if (res.status === 422) {
          try {
            const errBody = await res.json();
            if (typeof errBody.detail === 'string') userMessage = errBody.detail;
          } catch (parseErr) { /* fall through to the generic message */ }
        }
        const err = new Error(`Request failed (${res.status})`);
        err.userMessage = userMessage;
        throw err;
      }
      const body = await res.json();
      // Confirmed update: state comes from the server echo.
      setFinding((prev) => ({
        ...prev,
        disposition: {
          status: body.status,
          headline_at_disposition: body.headline_at_disposition,
          changed_since_accepted: false,
        },
      }));
      if (body.status === 'accepted') setAcceptedHint(true);
    } catch (err) {
      console.error('Disposition save failed:', err.message);
      setStatusError(err.userMessage || 'Could not save the decision. Please try again.');
    } finally {
      setSavingStatus(null);
    }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text || !finding) return;
    setNoteSaving(true);
    setNoteError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/opportunities/dispositions/${finding.identity_hash}/notes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ note_text: text }),
        },
      );
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const body = await res.json();
      setFinding((prev) => ({
        ...prev,
        notes: [
          ...(prev.notes || []),
          { note_id: body.note_id, note_text: text, created_at: body.created_at },
        ],
      }));
      setNoteText('');
    } catch (err) {
      console.error('Note save failed:', err.message);
      setNoteError('Could not add the note. Please try again.');
    } finally {
      setNoteSaving(false);
    }
  };

  const topbar = (material) => (
    <div className="opp-topbar">
      <div className="opp-crumb">
        <button type="button" className="linklike" onClick={() => navigate('/opportunities')}>Opportunities</button>
        {material && <><span className="sep">/</span><span className="cur">{material}</span></>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="opp">
        {topbar(null)}
        <div className="opp-wrap narrow"><div className="opp-empty" style={{ marginTop: '24px' }}>Loading finding...</div></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="opp">
        {topbar(null)}
        <div className="opp-wrap narrow">
          <div className="opp-empty" style={{ marginTop: '24px' }}>
            <div className="et">Finding not found</div>
            <div>This finding may belong to an older snapshot, or the module may be switched off.</div>
            <button type="button" className="opp-btn pri" style={{ marginTop: '14px' }} onClick={() => navigate('/opportunities')}>
              Back to Opportunities
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error || !finding) {
    return (
      <div className="opp">
        {topbar(null)}
        <div className="opp-wrap narrow">
          <div className="opp-empty" style={{ marginTop: '24px' }}>
            <div className="et">{error || 'Unable to load this finding.'}</div>
            <button type="button" className="opp-btn pri" style={{ marginTop: '14px' }} onClick={fetchFinding}>Try again</button>
          </div>
        </div>
      </div>
    );
  }

  const disp = finding.disposition;
  const changed = Boolean(disp && disp.changed_since_accepted);
  // Absent finding_class (pre-Lane-47 API) reads as active.
  const findingClass = finding.finding_class || 'active';
  // Lane 338: 'evidence' (the step-change family, Lane 323) is claim-free like
  // monitor/verify — no dollar headline, notes-only (the server refuses
  // dispositions with 422), the facts card instead of the savings math.
  const isClassified = findingClass === 'monitor' || findingClass === 'verify'
    || findingClass === 'evidence';
  // Lane 355 (O-2): the monitor badge is flavor-specific (see MONITOR_PRICE_GONE_BADGE).
  const classBadge = (findingClass === 'monitor' && isPriceGone(finding))
    ? MONITOR_PRICE_GONE_BADGE
    : CLASS_BADGES[findingClass];

  // Volume findings get the best-price dot (plan §2D); frozen values only.
  const frozenMath = (finding.detail_json || {}).math || {};
  const bestPoDate = finding.family_type === 'volume' ? (frozenMath.best_po_date || null) : null;
  const bestPrice = finding.family_type === 'volume'
    ? (frozenMath.best_price !== undefined ? frozenMath.best_price : null) : null;

  const detail = finding.detail_json || {};
  const hasSignals = Boolean(finding.family4_ppi || detail.inventory || detail.leadtime);

  // Lane 852: the material-wide, EVERY-SUPPLIER purchases in this window — the
  // rows the retired second PO table rendered, now read only by the fallback
  // table (Lane 1358 removed the every-supplier count from the finding's own
  // PO card on Ryan's instruction). `null` priceHistory (still loading) yields
  // [] so the fallback never claims rows it does not have.
  const materialWidePurchases = priceHistory === null
    ? []
    : windowPurchases(priceHistory, asOf, windowMonths);

  const notesBlock = (
    <div>
      <div className="opp-lbl" style={{ marginBottom: '10px' }}>Notes</div>
      {(finding.notes || []).length === 0 ? (
        <div className="opp-note" style={{ marginTop: 0, marginBottom: '12px' }}>No notes yet.</div>
      ) : (
        <div style={{ marginBottom: '12px' }}>
          {finding.notes.map((n) => (
            <div key={n.note_id} className="opp-nitem">
              <div className="nb">{n.note_text}</div>
              <div className="nm">{formatDate(n.created_at)}</div>
            </div>
          ))}
        </div>
      )}
      <div className="opp-notebox">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Add a note for the team (notes cannot be edited or deleted)"
          rows={3}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px' }}>
        <button type="button" className="opp-btn sm" onClick={addNote} disabled={noteSaving || !noteText.trim()}>
          {noteSaving ? 'Adding...' : 'Add note'}
        </button>
        {noteError && <span className="opp-err">{noteError}</span>}
      </div>
    </div>
  );

  return (
    <div className="opp">
      {topbar(finding.material_number)}
      <div className="opp-wrap narrow">
        <button type="button" className="opp-back" onClick={() => navigate('/opportunities')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
          All opportunities
        </button>

        {changed && !isClassified && (
          <div className="opp-banner">
            {`This finding has grown since you accepted it: ${formatUsd(disp.headline_at_disposition)} → ${formatUsd(finding.headline_value)}.`}
          </div>
        )}

        {/* HERO — material-first, a single downward-reading column (QA-1,
            2026-07-22): the cube/avatar box is removed (it shifted the text and
            made the header read unformatted), and the family-labelled
            opportunity dollar is pulled OUT of the far-right rail into the flow,
            directly below the framing — so the hero reads identity -> story ->
            the number -> provenance, then the math/charts/spend below it as one
            flow. meta-strip carries the material number but never a dollar (the
            L2 no-dollar-in-meta guard); the opportunity stat is hidden for
            classified findings (claim-free). */}
        <header className="opp-l2head">
          <div className="opp-idcol">
            <div className="opp-nmeta" data-testid="meta-strip">
              <span className="opp-matno">{finding.material_number}</span>
              <span className="opp-fbadge">{FAMILY_LABELS[finding.family_type] || finding.family_type}</span>
              {isClassified && classBadge && (
                <span className={`opp-cbadge ${classBadge.cls}`}>
                  {classBadge.label}
                </span>
              )}
            </div>
            {finding.material_name && <h1 className="opp-l2h1">{finding.material_name}</h1>}
            <div className="opp-cats">{finding.supplier_display}</div>
            {finding.ai_framing && <p className="opp-framing">{finding.ai_framing}</p>}
            {!isClassified && (
              <div className="opp-headstat">
                <div className="k">Opportunity</div>
                {/* QA R2-B (Mega QA 2 2026-07-22): drop the redundant "opportunity"
                    noun from the value -- the "Opportunity" header above already
                    says it (volume reads "$X/yr", not "$X/yr opportunity"). */}
                <div className="v">{headlineLabel(finding.family_type, finding.headline_value, { omitOpportunityNoun: true })}</div>
                {finding.net_value !== null && finding.net_value !== undefined && (
                  <div className="net">~{formatUsd(finding.net_value)} net of carrying cost</div>
                )}
              </div>
            )}
            <div className="opp-note" style={{ marginTop: '10px' }}>
              Snapshot of {formatDate(finding.created_at)} &middot; all figures frozen at snapshot time
            </div>
          </div>
        </header>

        {/* FINDINGS — the frozen figures panel. Lane 1358 (Ryan, 2026-09-30):
            it now spans the full page width (the 640px cap is gone) with the
            evidence table beside the figures, so it reads clearly as one
            page-wide block above the two charts. Every figure is
            byte-identical; this is layout only. */}
        <div className="opp-panel">
          {isClassified ? <ClassifiedFactsCard finding={finding} /> : <MathCard finding={finding} />}
        </div>

        {/* THE CHARTS — live current history (the figures above are frozen at
            snapshot). Lane 1358 (Ryan, 2026-09-30): the "The evidence" heading
            and its note are gone, and the two charts sit side by side as two
            equal blocks (OpportunityPriceChart renders both blocks; one chart
            alone spans the full width). The frozen-vs-current sentence still
            renders: inside the Blended PPI hover note, or under the chart when
            there is no market line.

            LANE 858 MOVED THIS UP, above the purchase-order table. Ryan, QA
            2026-08-05 on Lane 852's result: "It goes to the POs, and then it
            goes to the chart. I think I like the chart first and then the POs."
            Lane 852 had just consolidated two PO tables into one and put the
            survivor above the charts; the de-duplication was right and stands,
            only the order is inverted here. */}
        <section className="opp-sec">
          {priceHistory === null ? (
            <div className="opp-empty">Loading price history...</div>
          ) : (
            <OpportunityPriceChart
              priceHistory={priceHistory}
              priceImpact={priceImpact}
              supplierDisplay={finding.supplier_display}
              bestPoDate={bestPoDate}
              bestPrice={bestPrice}
              windowMonths={windowMonths}
              asOf={asOf}
            />
          )}
        </section>

        {/* PRICE VS. ORDER SIZE (controlled for date) — Lane 363. Renders only
            when the backend froze detail_json.volume_trend (volume active +
            price-gone monitor); absent -> nothing, so every other family +
            pre-fix snapshot is byte-identical.

            LANE 858: this is a CHART, and it stays with the charts — the PO
            table now follows BOTH of them. Putting the table between the two
            chart cards would have rebuilt, on volume findings, the exact
            chart/table/chart sandwich Lane 852 was dispatched to remove.
            "Charts first, then the POs" means all of them, not just the first. */}
        <VolumeTrendCard finding={finding} />

        {/* THE PURCHASE ORDERS — exactly ONE table, BELOW the charts (Lane 852
            consolidated it; Lane 858 put it below).
            The page used to render TWO per-PO tables with the charts sandwiched
            between them: this one (Lane 821, the finding's own evidence) and the
            material-wide "Purchase history" table below the charts. Ryan, QA
            2026-08-05: "You only need that once."

            WHICH ONE SURVIVES, and what that costs. The two tables are not the
            same rows: this one is scoped to the finding's OWN (material,
            supplier) pair on the engine's own filter+window, the other was
            material-wide across EVERY supplier. Column-for-column, this one is
            the superset in detail (PO number, warehouse, business unit, order
            date, line value, and the evidence flag) and the material-wide table
            carried exactly ONE column this one cannot: Supplier -- because this
            table is single-supplier by construction.

            That Supplier column, and the other suppliers' rows under it, are not
            dropped into nothing: the chart DIRECTLY ABOVE is the material-wide
            view. It draws one line PER SUPPLIER off the same `priceHistory`,
            names each in its legend, and its tooltip enumerates every PO on a
            day with that PO's own price and quantity -- i.e. it already carries
            all four of the retired table's columns, per PO, for every supplier.
            What is genuinely lost is the SCANNABLE TABULAR form of that, so the
            every-supplier count it used to state in its heading is preserved in
            words on this card (see the note in FindingPurchaseOrdersCard).

            Merging the two into one table was considered and rejected: the
            material-wide feed cannot fill PO number, warehouse, business unit or
            line value, so the other-suppliers' rows would render four of eight
            columns as dashes -- which reads as missing data rather than as a
            different universe, and would be a worse defect than the duplication.

            NOTHING IS RECOMPUTED. This is layout: every figure on both tables
            renders from the same fields, by the same formatters, as before. */}
        <FindingPurchaseOrdersCard data={findingPos} />

        {/* FALLBACK — the material-wide table, rendered ONLY when the finding's
            own table has nothing to show. It sits in the SAME slot as the card
            above it, so the chart-then-table order holds in this branch too:
            whichever table renders, it renders below the charts, and the page
            cannot end up chart-first in one state and table-first in the other.
            `findingPos` and `priceHistory` are independent fetches, so this is
            the case where
            the drill-down 404s or comes back empty; without this branch the page
            would show no per-PO table at all, which is a regression on the
            duplication it fixes. Exactly one of the two ever renders.

            This table does NOT flag (Lane 824). It used to append
            "(flagged unusual)" from `is_outlier`, which the shared
            /api/materials/{m}/price-history endpoint computes with
            `detect_outliers_iqr` on RAW DOLLAR prices at the default
            hard_floor_pct=100.0 -- a parameter documented as a PERCENTAGE floor
            (helpers/data.py:804-805) and fed percentages by every other caller.
            On dollars it becomes a +/-$100.00 floor, so for any material under
            ~$100/unit the bound is `median + $100` and the marker cannot fire
            however extreme the spike: measured on staging company 91, 56 of 77
            supplier price-groups have their upper bound set by that floor, and
            finding #143 flags a $0.7700 buy against a $0.0770 median (10x)
            while this table's bound sits at $100.08 and it says nothing.
            "Unusual" is therefore defined in exactly ONE place on this page --
            the finding's own frozen evidence -- and after Lane 852 the two
            tables cannot even co-render to disagree. The statistic itself is NOT
            fixed here: it is shared with MaterialDossier / Analytics /
            SupplierDetail and is filed as technical debt. */}
        {!findingPoTableRenders(findingPos) && materialWidePurchases.length > 0 && (
          <section className="opp-sec">
            <div className="opp-sec-h">
              <span className="opp-lbl">
                Purchase history - every supplier, last {windowMonths} months ({pluralize(materialWidePurchases.length, 'PO')})
              </span>
            </div>
            <table className="opp-tbl">
              <thead>
                <tr><th>Date</th><th>Supplier</th><th className="right">Unit price</th><th className="right">Quantity</th></tr>
              </thead>
              <tbody>
                {materialWidePurchases.map((p, i) => (
                  <tr key={i}>
                    <td>{formatDate(p.date)}</td>
                    <td className="opp-supcell">{p.supplier}</td>
                    <td className="num">{formatUnitPrice(p.price)}</td>
                    <td className="num">{p.quantity ? formatQty(p.quantity) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* DECISION + NOTES (notes-only on classified pages — the server also
            refuses dispositions with 422). */}
        <section className="opp-sec">
          <div className={`opp-decide${isClassified ? ' solo' : ''}`}>
            {!isClassified && (
              <div>
                <div className="opp-lbl" style={{ marginBottom: '10px' }}>Team decision</div>
                <div className="opp-dbtns">
                  {['pursuing', 'needs_data', 'accepted'].map((s) => {
                    const active = disp && disp.status === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        className={`opp-dbtn${active ? ' on' : ''}`}
                        disabled={savingStatus !== null}
                        onClick={() => setDisposition(s)}
                      >
                        {savingStatus === s ? 'Saving...' : DISPOSITION_LABELS[s]}
                      </button>
                    );
                  })}
                </div>
                <div className="opp-note">
                  {disp
                    ? `${DISPOSITION_LABELS[disp.status] || disp.status}: ${STATUS_HELP[disp.status] || ''} Decided at ${formatUsd(disp.headline_at_disposition)}.`
                    : 'No decision yet.'}
                </div>
                {acceptedHint && (
                  <div className="opp-hint" style={{ marginTop: '6px' }}>
                    Accepted items are hidden from the default list unless they grow materially.
                  </div>
                )}
                {statusError && <div className="opp-err" style={{ marginTop: '6px' }}>{statusError}</div>}
              </div>
            )}
            <div>{notesBlock}</div>
          </div>
        </section>

        {/* SEPARATE SIGNALS — market context + inventory + lead-time reality,
            each a DIFFERENT signal than the opportunity math (plan §2G). */}
        {hasSignals && (
          <section className="opp-sec">
            <div className="opp-sec-h">
              <span className="opp-lbl">Separate signals</span>
              <span className="note">context alongside the finding, not part of the dollar claim</span>
            </div>
            <div className="opp-cols">
              <MarketContextCard family4={finding.family4_ppi} />
              <InventoryPositionCard finding={finding} />
              <LeadtimeRealityCard finding={finding} />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default OpportunityFinding;
